import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  ConfirmDialog,
  dangerButtonClass,
  Field,
  FormActions,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  QUANTITY_STEP,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

interface MaterialLine {
  productId: string;
  sku: string;
  name: string;
  unit: string;
  quantity: number;
  unitCost: number;
  amount: number;
}
interface OperationLine {
  productId: string;
  productName: string;
  quantity: number;
  sequence: number;
  operation: string;
  workCenter: string | null;
  isOutsourced: boolean;
  minutes: number;
  hourlyCost: number;
  setupCost: number;
  amount: number;
}
interface Sheet {
  id?: string;
  costSheetNumber?: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  listPrice: number;
  bomNumber: string | null;
  quantity: number;
  materials: MaterialLine[];
  operations: OperationLine[];
  materialCost: number;
  conversionCost: number;
  overheadPercent: number;
  overheadAmount: number;
  otherCost: number;
  otherCostNote: string | null;
  totalCost: number;
  markupPercent: number;
  unitCost: number;
  unitPrice: number;
  totalPrice: number;
  marginOnPricePercent: number;
  warnings: string[];
  notes: string | null;
  quoteNumber?: string | null;
  quoteCustomer?: string | null;
  salesOrderId?: string | null;
  createdBy?: string | null;
  createdAt?: string;
}
interface SheetRow {
  id: string;
  costSheetNumber: string;
  createdAt: string;
  productName: string;
  sku: string;
  unit: string;
  bomNumber: string | null;
  quantity: number;
  totalCost: number;
  unitCost: number;
  markupPercent: number;
  unitPrice: number;
  quoteNumber: string | null;
  warnings: number;
}
interface SheetPage {
  data: SheetRow[];
  total: number;
  totalPages: number;
}
interface ProductChoice {
  id: string;
  name: string;
  sku: string;
  unit: string;
}
interface BomChoice {
  id: string;
  bomNumber: string;
  version: number;
  revision: string;
}
interface Choice {
  id: string;
  name: string;
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const cellHead = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

const costingApi = {
  page: async (params: object): Promise<SheetPage> => (await api.get('/cost-sheets', { params: clean(params) })).data,
  one: async (id: string): Promise<Sheet> => (await api.get(`/cost-sheets/${id}`)).data,
  preview: async (data: object): Promise<Sheet> => (await api.post('/cost-sheets/preview', data)).data,
  create: async (data: object): Promise<Sheet> => (await api.post('/cost-sheets', data)).data,
  quote: async (id: string, data: object): Promise<Sheet> => (await api.post(`/cost-sheets/${id}/quote`, data)).data,
  remove: async (id: string): Promise<void> => (await api.delete(`/cost-sheets/${id}`)).data,
  products: async (search: string): Promise<ProductChoice[]> => (await api.get('/inventory/paginated', { params: { search, limit: 8, page: 1 } })).data.data,
  boms: async (productId: string): Promise<BomChoice[]> => (await api.get('/manufacturing/engineering-boms', { params: { productId } })).data,
  customers: async (): Promise<Choice[]> => {
    const result = (await api.get('/customers', { params: { limit: 100 } })).data;
    return (Array.isArray(result) ? result : result?.data ?? []) as Choice[];
  },
};

// ---------------------------------------------------------------- the worked-out sheet

const SheetBody: React.FC<{ sheet: Sheet }> = ({ sheet }) => (
  <div className="space-y-4">
    {sheet.warnings.map((warning) => (
      <p key={warning} className="rounded-md bg-amber-50 px-4 py-2.5 text-sm text-amber-900 ring-1 ring-amber-200">
        {warning}
      </p>
    ))}

    <section className="overflow-hidden rounded-lg border border-gray-200">
      <h4 className="flex justify-between border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        <span>Material{sheet.bomNumber ? ` · from ${sheet.bomNumber}, exploded to what is bought` : ''}</span>
        <span className="text-gray-900">{formatMoney(sheet.materialCost)}</span>
      </h4>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th scope="col" className={cellHead}>Item</th>
              <th scope="col" className={cn(cellHead, 'text-right')}>Quantity</th>
              <th scope="col" className={cn(cellHead, 'text-right')}>Cost price</th>
              <th scope="col" className={cn(cellHead, 'text-right')}>Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sheet.materials.map((line) => (
              <tr key={line.productId}>
                <td className="px-3 py-1.5 text-sm text-gray-900">
                  {line.name} <span className="text-xs text-gray-500">{line.sku}</span>
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm text-gray-700 tabular-nums">
                  {formatNumber(line.quantity, 6)} {line.unit}
                </td>
                <td className={cn('whitespace-nowrap px-3 py-1.5 text-right text-sm tabular-nums', line.unitCost === 0 ? 'font-medium text-red-600' : 'text-gray-700')}>{formatMoney(line.unitCost)}</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm text-gray-900 tabular-nums">{formatMoney(line.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    <section className="overflow-hidden rounded-lg border border-gray-200">
      <h4 className="flex justify-between border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        <span>Conversion · labour and machine time from the routings</span>
        <span className="text-gray-900">{formatMoney(sheet.conversionCost)}</span>
      </h4>
      {sheet.operations.length === 0 ? (
        <p className="px-3 py-3 text-sm text-gray-500">No routing operations to cost.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th scope="col" className={cellHead}>Operation</th>
                <th scope="col" className={cellHead}>Work center</th>
                <th scope="col" className={cn(cellHead, 'text-right')}>Minutes</th>
                <th scope="col" className={cn(cellHead, 'text-right')}>Per hour</th>
                <th scope="col" className={cn(cellHead, 'text-right')}>Setup cost</th>
                <th scope="col" className={cn(cellHead, 'text-right')}>Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sheet.operations.map((line) => (
                <tr key={`${line.productId}-${line.sequence}`}>
                  <td className="px-3 py-1.5 text-sm text-gray-900">
                    {line.sequence} · {line.operation}
                    <span className="block text-xs text-gray-500">
                      {line.productName} × {formatNumber(line.quantity)}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-sm text-gray-700">{line.isOutsourced ? 'Outside job work' : line.workCenter ?? '—'}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm text-gray-700 tabular-nums">{formatNumber(line.minutes, 2)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm text-gray-700 tabular-nums">{line.isOutsourced ? '—' : formatMoney(line.hourlyCost)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm text-gray-700 tabular-nums">{line.isOutsourced ? '—' : formatMoney(line.setupCost)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm text-gray-900 tabular-nums">{line.isOutsourced ? 'Not counted' : formatMoney(line.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>

    <dl className="ml-auto max-w-md rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
      {[
        ['Material', formatMoney(sheet.materialCost)],
        ['Conversion', formatMoney(sheet.conversionCost)],
        [`Overheads · ${formatNumber(sheet.overheadPercent, 2)}% of the two`, formatMoney(sheet.overheadAmount)],
        [`Other costs${sheet.otherCostNote ? ` · ${sheet.otherCostNote}` : ''}`, formatMoney(sheet.otherCost)],
      ].map(([label, value]) => (
        <div key={label} className="flex justify-between gap-4 py-1">
          <dt className="text-gray-600">{label}</dt>
          <dd className="text-gray-900 tabular-nums">{value}</dd>
        </div>
      ))}
      <div className="mt-1 flex justify-between gap-4 border-t border-gray-200 pt-2">
        <dt className="font-semibold text-gray-900">
          Total cost for {formatNumber(sheet.quantity)} {sheet.unit}
        </dt>
        <dd className="font-semibold text-gray-900 tabular-nums">{formatMoney(sheet.totalCost)}</dd>
      </div>
      <div className="flex justify-between gap-4 py-1">
        <dt className="text-gray-600">Cost per {sheet.unit}</dt>
        <dd className="text-gray-900 tabular-nums">{formatMoney(sheet.unitCost)}</dd>
      </div>
      <div className="flex justify-between gap-4 py-1">
        <dt className="text-gray-600">Markup · {formatNumber(sheet.markupPercent, 2)}% on cost</dt>
        <dd className="text-gray-900 tabular-nums">{formatMoney(sheet.unitPrice - sheet.unitCost)}</dd>
      </div>
      <div className="mt-1 flex justify-between gap-4 border-t border-gray-200 pt-2">
        <dt className="text-base font-semibold text-gray-900">Price per {sheet.unit}</dt>
        <dd className="text-base font-semibold text-gray-900 tabular-nums">{formatMoney(sheet.unitPrice)}</dd>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        {formatMoney(sheet.totalPrice)} for the quantity · {formatNumber(sheet.marginOnPricePercent, 1)}% of the price is margin · the item master carries {formatMoney(sheet.listPrice)}
      </p>
    </dl>
  </div>
);

// ---------------------------------------------------------------- a new sheet, worked out as it is filled in

const SheetForm: React.FC<{ canSave: boolean; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ canSave, isPending, onCancel, onSubmit }) => {
  const [product, setProduct] = useState<ProductChoice | null>(null);
  const [form, setForm] = useState({ quantity: '1', bomHeaderId: '', overheadPercent: '', otherCost: '', otherCostNote: '', markupPercent: '', notes: '' });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [request, setRequest] = useState<object | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const { data: choices = [], isFetching: searching } = useQuery({ queryKey: ['costSheets', 'products', search], queryFn: () => costingApi.products(search), enabled: !product && search.length >= 2 });
  const { data: boms = [] } = useQuery({ queryKey: ['costSheets', 'boms', product?.id], queryFn: () => costingApi.boms(product!.id), enabled: !!product });

  const number = (value: string) => (value.trim() === '' ? 0 : Number(value));
  const quantity = Number(form.quantity);
  const valid = !!product && Number.isFinite(quantity) && quantity > 0 && [form.overheadPercent, form.otherCost, form.markupPercent].every((value) => Number.isFinite(number(value)) && number(value) >= 0);
  // The sheet is worked out again a moment after the last change
  useEffect(() => {
    if (!valid || !product) {
      setRequest(null);
      return undefined;
    }
    const timer = setTimeout(
      () =>
        setRequest({
          productId: product.id,
          quantity,
          bomHeaderId: form.bomHeaderId || null,
          overheadPercent: number(form.overheadPercent),
          otherCost: number(form.otherCost),
          otherCostNote: form.otherCostNote.trim() || null,
          markupPercent: number(form.markupPercent),
          notes: form.notes.trim() || null,
        }),
      400,
    );
    return () => clearTimeout(timer);
  }, [valid, product, quantity, form]);
  const { data: sheet, isFetching, error } = useQuery({ queryKey: ['costSheets', 'preview', request], queryFn: () => costingApi.preview(request as object), enabled: !!request, placeholderData: keepPreviousData, retry: false });

  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm((prev) => ({ ...prev, [field]: event.target.value }));
  const numberBox = (field: 'overheadPercent' | 'otherCost' | 'markupPercent') => cn(inputClass, 'text-right tabular-nums', !(Number.isFinite(number(form[field])) && number(form[field]) >= 0) && inputErrorClass);

  return (
    <div className="space-y-4">
      <Field label="Product" htmlFor="cs-product" required>
        {product ? (
          <div className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2 ring-1 ring-gray-200">
            <p className="truncate text-sm font-medium text-gray-900">
              {product.name} <span className="font-normal text-gray-500">{product.sku}</span>
            </p>
            <button type="button" onClick={() => { setProduct(null); setForm((prev) => ({ ...prev, bomHeaderId: '' })); }} className="shrink-0 text-xs font-semibold text-indigo-600 hover:text-indigo-500">
              Change
            </button>
          </div>
        ) : (
          <div>
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input id="cs-product" type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search product or SKU to cost" className={cn(inputClass, 'pl-9')} />
            </div>
            {search.length >= 2 && (
              <ul className="mt-1 max-h-44 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                {choices.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-gray-500">{searching ? 'Searching…' : 'No product matches'}</li>
                ) : (
                  choices.map((row) => (
                    <li key={row.id}>
                      <button type="button" onClick={() => { setProduct(row); setSearchInput(''); setSearch(''); }} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-indigo-50">
                        <span>
                          <span className="font-medium text-gray-900">{row.name}</span> <span className="text-gray-500">{row.sku}</span>
                        </span>
                        <span className="text-xs text-gray-500">{row.unit}</span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        )}
      </Field>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <Field label="Quantity" htmlFor="cs-quantity" required>
          <input id="cs-quantity" type="number" min="0" step={QUANTITY_STEP} value={form.quantity} onChange={set('quantity')} className={cn(inputClass, 'text-right tabular-nums', !(Number.isFinite(quantity) && quantity > 0) && inputErrorClass)} />
        </Field>
        <Field label="BOM" htmlFor="cs-bom" className="lg:col-span-2" hint={product && boms.length === 0 ? 'No approved BOM: costed at its cost price' : undefined}>
          <select id="cs-bom" value={form.bomHeaderId} onChange={set('bomHeaderId')} disabled={boms.length === 0} className={inputClass}>
            <option value="">{boms.length === 0 ? 'None' : 'The approved BOM'}</option>
            {boms.map((bom) => (
              <option key={bom.id} value={bom.id}>
                {bom.bomNumber} · version {bom.version}
                {bom.revision}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Overheads %" htmlFor="cs-overhead" hint="Of material + conversion">
          <input id="cs-overhead" type="number" min="0" step="0.01" value={form.overheadPercent} onChange={set('overheadPercent')} placeholder="0" className={numberBox('overheadPercent')} />
        </Field>
        <Field label="Other costs (₹)" htmlFor="cs-other" hint="For the whole quantity">
          <input id="cs-other" type="number" min="0" step="0.01" value={form.otherCost} onChange={set('otherCost')} placeholder="0" className={numberBox('otherCost')} />
        </Field>
        <Field label="Markup %" htmlFor="cs-markup" hint="Added on top of cost">
          <input id="cs-markup" type="number" min="0" step="0.01" value={form.markupPercent} onChange={set('markupPercent')} placeholder="0" className={numberBox('markupPercent')} />
        </Field>
        <Field label="Other costs are for" htmlFor="cs-other-note" className="col-span-2 lg:col-span-3">
          <input id="cs-other-note" type="text" maxLength={200} value={form.otherCostNote} onChange={set('otherCostNote')} placeholder="Tooling, packing, freight, outside job work" className={inputClass} />
        </Field>
        <Field label="Notes" htmlFor="cs-notes" className="col-span-2 lg:col-span-3">
          <input id="cs-notes" type="text" maxLength={500} value={form.notes} onChange={set('notes')} className={inputClass} />
        </Field>
      </div>

      {!product ? (
        <p className="rounded-md bg-gray-50 px-4 py-6 text-center text-sm text-gray-500 ring-1 ring-gray-200">Choose a product to see what it costs to make.</p>
      ) : error ? (
        <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800 ring-1 ring-red-200">{getApiErrorMessage(error, 'The cost could not be worked out.')}</p>
      ) : !sheet ? (
        <p className="py-6 text-center text-sm text-gray-500">Working it out…</p>
      ) : (
        <div className={cn(isFetching && 'opacity-60')}>
          <SheetBody sheet={sheet} />
        </div>
      )}

      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        {canSave && (
          <button type="button" disabled={isPending || !request || !sheet || !!error || isFetching} onClick={() => onSubmit(request as object)} className={primaryButtonClass}>
            {isPending ? 'Saving…' : 'Save Cost Sheet'}
          </button>
        )}
      </FormActions>
    </div>
  );
};

// ---------------------------------------------------------------- a saved sheet

const SheetPanel: React.FC<{ sheetId: string; canQuote: boolean; canDelete: boolean; onClose: () => void; onChanged: () => void; onDelete: (sheet: Sheet) => void }> = ({ sheetId, canQuote, canDelete, onClose, onChanged, onDelete }) => {
  const queryClient = useQueryClient();
  const { data: sheet, isLoading, isError, error } = useQuery({ queryKey: ['costSheets', 'one', sheetId], queryFn: () => costingApi.one(sheetId) });
  const { data: customers = [] } = useQuery({ queryKey: ['costSheets', 'customers'], queryFn: costingApi.customers, enabled: canQuote });
  const [quoting, setQuoting] = useState(false);
  const [quote, setQuote] = useState({ customerId: '', expectedDeliveryDate: '', quoteValidUntil: '' });
  const raise = useMutation({
    mutationFn: () => costingApi.quote(sheetId, { customerId: quote.customerId, expectedDeliveryDate: quote.expectedDeliveryDate, quoteValidUntil: quote.quoteValidUntil || null }),
    onSuccess: (saved) => {
      toast.success(`Quotation ${saved.quoteNumber} raised at ${formatMoney(saved.unitPrice)} per ${saved.unit}`);
      queryClient.setQueryData(['costSheets', 'one', sheetId], saved);
      queryClient.invalidateQueries({ queryKey: ['salesOrders'] });
      setQuoting(false);
      onChanged();
    },
    onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The quotation could not be raised')),
  });

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !sheet) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The cost sheet could not be loaded.')}</p>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-700">
        <span className="font-semibold text-gray-900">{sheet.productName}</span> <span className="text-gray-500">{sheet.sku}</span> · {formatNumber(sheet.quantity)} {sheet.unit} · worked out {formatDate(sheet.createdAt)}
        {sheet.createdBy ? ` by ${sheet.createdBy}` : ''}. The figures are kept as they were on that day.
        {sheet.notes && <span className="block text-gray-500">{sheet.notes}</span>}
      </p>
      {sheet.quoteNumber && (
        <p className="rounded-md bg-green-50 px-4 py-2.5 text-sm text-green-900 ring-1 ring-green-200">
          Quoted to {sheet.quoteCustomer} on{' '}
          <Link to="/quotes" className="font-semibold underline">
            {sheet.quoteNumber}
          </Link>
          .
        </p>
      )}
      <SheetBody sheet={sheet} />

      {quoting ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="mb-3 text-sm text-gray-700">
            A quotation for {formatNumber(sheet.quantity)} {sheet.unit} at {formatMoney(sheet.unitPrice)} each.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Customer" htmlFor="cq-customer" required>
              <select id="cq-customer" value={quote.customerId} onChange={(event) => setQuote((prev) => ({ ...prev, customerId: event.target.value }))} className={inputClass}>
                <option value="">Choose…</option>
                {customers.map((customer) => (<option key={customer.id} value={customer.id}>{customer.name}</option>))}
              </select>
            </Field>
            <Field label="Expected delivery" htmlFor="cq-delivery" required>
              <input id="cq-delivery" type="date" min={today()} value={quote.expectedDeliveryDate} onChange={(event) => setQuote((prev) => ({ ...prev, expectedDeliveryDate: event.target.value }))} className={inputClass} />
            </Field>
            <Field label="Prices valid until" htmlFor="cq-valid" hint="Optional">
              <input id="cq-valid" type="date" min={today()} value={quote.quoteValidUntil} onChange={(event) => setQuote((prev) => ({ ...prev, quoteValidUntil: event.target.value }))} className={inputClass} />
            </Field>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setQuoting(false)} className={secondaryButtonClass}>
              Back
            </button>
            <button type="button" disabled={raise.isPending || !quote.customerId || !quote.expectedDeliveryDate} onClick={() => raise.mutate()} className={primaryButtonClass}>
              {raise.isPending ? 'Raising…' : 'Confirm Quotation'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3">
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Close Window
          </button>
          {!sheet.salesOrderId && canDelete && (
            <button type="button" onClick={() => onDelete(sheet)} className={dangerButtonClass}>
              Delete
            </button>
          )}
          {!sheet.salesOrderId && canQuote && (
            <button type="button" onClick={() => setQuoting(true)} disabled={sheet.unitPrice <= 0} className={primaryButtonClass}>
              Raise Quotation
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- the page

const CostSheets: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('sales.create');
  const canDelete = can('sales.delete');
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Sheet | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching } = useQuery({ queryKey: ['costSheets', 'page', { page, limit, search }], queryFn: () => costingApi.page({ page, limit, search }), placeholderData: keepPreviousData });
  const { data: viewing } = useQuery({ queryKey: ['costSheets', 'one', viewingId], queryFn: () => costingApi.one(viewingId as string), enabled: !!viewingId });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['costSheets', 'page'] });

  const create = useMutation({
    mutationFn: costingApi.create,
    onSuccess: (sheet) => {
      toast.success(`${sheet.costSheetNumber} saved`);
      queryClient.setQueryData(['costSheets', 'one', sheet.id], sheet);
      refresh();
      setFormOpen(false);
      setViewingId(sheet.id as string);
    },
    onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The cost sheet could not be saved')),
  });
  const remove = useMutation({
    mutationFn: (sheet: Sheet) => costingApi.remove(sheet.id as string),
    onSuccess: () => {
      toast.success('Cost sheet deleted');
      refresh();
      setDeleting(null);
      setViewingId(null);
    },
    onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The cost sheet could not be deleted')),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Costing</h1>
          <p className="mt-1 text-sm text-gray-500">What a quantity of a product costs to make, from its BOM and routing, and the price that follows once overheads and a markup are added.</p>
        </div>
        <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
          <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
          New Cost Sheet
        </button>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="border-b border-gray-200 p-4">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by sheet number, product, BOM or quotation" aria-label="Search cost sheets" className={cn(inputClass, 'pl-9')} />
          </div>
        </div>

        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Cost sheet</th>
                <th scope="col" className={thClass}>Product</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Quantity</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Cost each</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Markup</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Price each</th>
                <th scope="col" className={thClass}>Quotation</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The cost sheets could not be loaded.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{search ? 'No cost sheet matches' : 'No cost sheets yet'}</p>
                    <p className="mt-1 text-sm text-gray-500">Work out a cost sheet before quoting a made-to-order item.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3">
                      <button type="button" onClick={() => setViewingId(row.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.costSheetNumber}</button>
                      <p className="mt-0.5 text-xs text-gray-500">{formatDate(row.createdAt)}</p>
                    </td>
                    <td className="max-w-xs px-4 py-3">
                      <p className="truncate text-sm text-gray-900">{row.productName}</p>
                      <p className="mt-0.5 truncate text-xs text-gray-500">
                        {row.sku} · {row.bomNumber ?? 'no BOM'}
                        {row.warnings > 0 && <span className="ml-2 font-medium text-amber-700">{row.warnings} note(s)</span>}
                      </p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                      {formatNumber(row.quantity)} <span className="text-gray-500">{row.unit}</span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatMoney(row.unitCost)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(row.markupPercent, 2)}%</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">{formatMoney(row.unitPrice)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{row.quoteNumber ?? '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setViewingId(row.id)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Open ${row.costSheetNumber}`}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal isOpen={(formOpen || !!viewingId) && !deleting} onClose={() => (formOpen ? setFormOpen(false) : setViewingId(null))} title={formOpen ? 'New Cost Sheet' : viewing?.costSheetNumber ?? 'Cost Sheet'} size="2xl">
        {formOpen ? (
          <SheetForm canSave={canCreate} isPending={create.isPending} onCancel={() => setFormOpen(false)} onSubmit={(input) => create.mutate(input)} />
        ) : (
          viewingId && <SheetPanel key={viewingId} sheetId={viewingId} canQuote={canCreate} canDelete={canDelete} onClose={() => setViewingId(null)} onChanged={refresh} onDelete={setDeleting} />
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!deleting}
        title="Delete the cost sheet?"
        message={deleting ? `${deleting.costSheetNumber} for ${deleting.productName} will be removed.` : ''}
        confirmLabel="Delete"
        onConfirm={() => deleting && remove.mutate(deleting)}
        onClose={() => setDeleting(null)}
        isPending={remove.isPending}
      />
    </div>
  );
};

export default CostSheets;
